import { parsePurchasePrice } from "../../helpers/collectorUpload";
import { storedMasterValue } from "../../helpers/collectorMasters";

/**
 * Title the server stores for a piece saved from a photo alone (backend CollectorItem.UNTITLED_TITLE).
 * It means "not named yet": the editor shows an empty field and the placard prints no title.
 */
export const UNTITLED_PIECE_TITLE = "Untitled piece";

/** The owner's own name for the piece, or "" when it has none yet. */
export const ownerPieceTitle = (title?: string | null): string => {
  const trimmed = String(title || "").trim();
  return trimmed === UNTITLED_PIECE_TITLE ? "" : trimmed;
};

export type CollectorPurchaseGeo = {
  lat: number;
  lng: number;
  accuracy?: number;
  capturedAt?: string;
  consentAt?: string;
};

export type CollectorRegulatoryDoc = {
  url: string;
  fileName: string;
  contentType?: string;
  uploadedAt?: string;
};

export type CollectorPieceValues = {
  title: string;
  coverPhotoUrl: string;
  story: string;
  itemCategory: string;
  color: string;
  craftType: string;
  fabric: string;
  clusterType: string;
  occasion: string;
  acquiredDate: string;
  acquiredPlace: string;
  visibility: string;
  publicDisplayFields: string[];
  additionalImageUrls: string[];
  regulatoryDocs: CollectorRegulatoryDoc[];
  makerName: string;
  purchasePrice: string;
  purchaseCurrency: string;
  purchaseGeo: CollectorPurchaseGeo | null;
};

export const emptyCollectorPieceValues = (): CollectorPieceValues => ({
  title: "",
  coverPhotoUrl: "",
  story: "",
  itemCategory: "",
  color: "",
  craftType: "",
  fabric: "",
  clusterType: "",
  occasion: "",
  acquiredDate: "",
  acquiredPlace: "",
  visibility: "PUBLIC",
  publicDisplayFields: [],
  additionalImageUrls: [],
  regulatoryDocs: [],
  makerName: "",
  purchasePrice: "",
  purchaseCurrency: "INR",
  purchaseGeo: null,
});

export const collectorPieceFromItem = (found: any): CollectorPieceValues => ({
  ...emptyCollectorPieceValues(),
  title: ownerPieceTitle(found.title),
  coverPhotoUrl: found.coverPhotoUrl || "",
  story: found.story || "",
  itemCategory: found.itemCategory || "",
  color: found.color || "",
  craftType: found.craftType || "",
  fabric: found.fabric || "",
  clusterType: found.clusterType || "",
  occasion: found.occasion || "",
  acquiredDate: found.acquiredDate || "",
  acquiredPlace: found.acquiredPlace || "",
  publicDisplayFields: found.publicDisplayFields || [],
  visibility: found.visibility || "PUBLIC",
  additionalImageUrls: found.additionalImageUrls || [],
  regulatoryDocs: found.regulatoryDocs || [],
  makerName: found.makerName || "",
  purchasePrice: found.purchasePrice != null && found.purchasePrice !== "" ? String(found.purchasePrice) : "",
  purchaseCurrency: found.purchaseCurrency || "INR",
  purchaseGeo: found.purchaseGeo?.lat != null && found.purchaseGeo?.lng != null
    ? {
        lat: Number(found.purchaseGeo.lat),
        lng: Number(found.purchaseGeo.lng),
        accuracy: found.purchaseGeo.accuracy != null ? Number(found.purchaseGeo.accuracy) : undefined,
        capturedAt: found.purchaseGeo.capturedAt,
        consentAt: found.purchaseGeo.consentAt,
      }
    : null,
});

export function collectorJournalBody(form: CollectorPieceValues): Record<string, unknown> {
  const purchasePrice = parsePurchasePrice(form.purchasePrice);
  return {
    title: form.title.trim(),
    coverPhotoUrl: form.coverPhotoUrl || undefined,
    story: form.story || undefined,
    itemCategory: storedMasterValue(form.itemCategory) || undefined,
    color: storedMasterValue(form.color) || undefined,
    craftType: storedMasterValue(form.craftType) || undefined,
    fabric: storedMasterValue(form.fabric) || undefined,
    clusterType: storedMasterValue(form.clusterType) || undefined,
    occasion: storedMasterValue(form.occasion) || undefined,
    acquiredDate: form.acquiredDate || undefined,
    acquiredPlace: form.acquiredPlace || undefined,
    visibility: form.visibility,
    publicDisplayFields: form.publicDisplayFields,
    makerName: form.makerName.trim() || null,
    purchasePrice,
    purchaseCurrency: purchasePrice == null ? null : (form.purchaseCurrency || "INR"),
    purchaseGeo: form.purchaseGeo || null,
    additionalImageUrls: form.additionalImageUrls,
    regulatoryDocs: form.regulatoryDocs,
  };
}
